// Restricted user access control utilities (workshop and freemium)

export interface User {
  type?: 'smb' | 'workshop' | 'freemium';
  role?: string;
}

// Define routes that workshop users can access
export const WORKSHOP_ACCESSIBLE_ROUTES = [
  '/tools/focus-finder',
  '/tools/13-week-cashflow-plan', 
  '/tools/cashflow-acceleration-process',
  '/tools/customer-avatar-builder',
  '/vision',
  '/tools/company-creed',
  '/tools/duplicate-yourself',
  '/tools/set-working-culture',
  '/tools/quarterly-goal-builder',
  '/tools/zero-based-budgeting',
  '/tools/vendor-discounts'
] as const;

// Define tool IDs that workshop users can access (for tool cards)
export const WORKSHOP_ACCESSIBLE_TOOL_IDS = [
  'focus-finder',
  '13-week-cashflow-plan',
  'cashflow-acceleration-process',
  'customer-avatar-builder', 
  'company-creed',
  'duplicate-yourself',
  'set-working-culture',
  'quarterly-goal-builder',
  'zero-based-budgeting',
  'vendor-discounts'
] as const;

// Define routes that freemium users can access (Focus Finder + all Finance tools + module pages for viewing)
export const FREEMIUM_ACCESSIBLE_ROUTES = [
  '/tools/focus-finder',
  '/finance', // Finance module page
  '/vision', // Vision module page (can view, tools restricted)
  '/models', // Models module page (can view, tools restricted)
  '/tempo', // Tempo module page (can view, tools restricted)
  '/people', // People module page (can view, tools restricted)
  '/ops', // Ops module page (can view, tools restricted)
  '/marketing-sales', // Marketing & Sales module page (can view, tools restricted)
  '/tools/13-week-cashflow-plan',
  '/tools/cashflow-acceleration-process',
  '/tools/zero-based-budgeting',
  '/tools/vendor-discounts',
  '/tools/dynamic-pl',
  '/tools/dynamic-financial-dashboard'
] as const;

// Define tool IDs that freemium users can access (for tool cards)
export const FREEMIUM_ACCESSIBLE_TOOL_IDS = [
  'focus-finder',
  '13-week-cashflow-plan',
  'cashflow-acceleration-process',
  'zero-based-budgeting',
  'vendor-discounts',
  'dynamic-pl',
  'dynamic-financial-dashboard'
] as const;

/**
 * Check if a user is a workshop user
 */
export const isWorkshopUser = (user: User | null | undefined): boolean => {
  return user?.type === 'workshop';
};

/**
 * Check if a user is a freemium user
 */
export const isFreemiumUser = (user: User | null | undefined): boolean => {
  return user?.type === 'freemium';
};

/**
 * Check if a user is a restricted user (workshop or freemium)
 */
export const isRestrictedUser = (user: User | null | undefined): boolean => {
  return isWorkshopUser(user) || isFreemiumUser(user);
};

/**
 * Check if a workshop user can access a specific route
 */
export const canWorkshopUserAccessRoute = (route: string): boolean => {
  return WORKSHOP_ACCESSIBLE_ROUTES.includes(route as any);
};

/**
 * Check if a workshop user can access a specific tool by ID
 */
export const canWorkshopUserAccessTool = (toolId: string): boolean => {
  return WORKSHOP_ACCESSIBLE_TOOL_IDS.includes(toolId as any);
};

/**
 * Check if a freemium user can access a specific route
 */
export const canFreemiumUserAccessRoute = (route: string): boolean => {
  return FREEMIUM_ACCESSIBLE_ROUTES.includes(route as any);
};

/**
 * Check if a freemium user can access a specific tool by ID
 */
export const canFreemiumUserAccessTool = (toolId: string): boolean => {
  return FREEMIUM_ACCESSIBLE_TOOL_IDS.includes(toolId as any);
};

/**
 * Check if a user has access to a route (handles restricted and non-restricted users)
 */
export const hasRouteAccess = (user: User | null | undefined, route: string): boolean => {
  // Non-restricted users (SMB) have access to everything
  if (!isRestrictedUser(user)) {
    return true;
  }
  
  // Check access based on user type
  if (isWorkshopUser(user)) {
    return canWorkshopUserAccessRoute(route);
  }
  
  if (isFreemiumUser(user)) {
    return canFreemiumUserAccessRoute(route);
  }
  
  return false;
};

/**
 * Check if a user has access to a tool (handles restricted and non-restricted users)
 */
export const hasToolAccess = (user: User | null | undefined, toolId: string): boolean => {
  // Non-restricted users (SMB) have access to everything
  if (!isRestrictedUser(user)) {
    return true;
  }
  
  // Check access based on user type
  if (isWorkshopUser(user)) {
    return canWorkshopUserAccessTool(toolId);
  }
  
  if (isFreemiumUser(user)) {
    return canFreemiumUserAccessTool(toolId);
  }
  
  return false;
};

/**
 * Get the access restriction message for a tool
 */
export const getToolAccessRestrictionMessage = (user: User | null | undefined, toolId: string): string | null => {
  if (hasToolAccess(user, toolId)) {
    return null;
  }
  
  return 'Growth Boardroom Only';
};
